import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiSearch, FiX } from 'react-icons/fi';
import Input from './Input';

/**
 * Debounced search input. Controlled from outside via `value`/`onChange`;
 * onChange fires after `debounce` ms of inactivity (0 = immediate).
 */
export default function SearchInput({ value = '', onChange, placeholder, debounce = 300, className = '', ...rest }) {
  const { t } = useTranslation();
  const [local, setLocal] = useState(value);
  const timer = useRef(null);

  useEffect(() => {
    if (local === value) return;
    setLocal(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    // Say nothing when the parent already holds this exact value. That covers
    // the mount, a StrictMode effect re-run, the parent echoing back the term
    // we just emitted, and the parent's own reset landing in `local`.
    //
    // This used to be a one-shot `skipNext` ref, and a flag cannot tell those
    // four apart. It was armed on every incoming `value` including the echo of
    // our own emit — where `local` had not changed, so the flag survived to eat
    // the user's NEXT edit instead. Clearing the box did nothing at all: the
    // empty string never reached onChange and the parent kept the old term.
    // Arming it only on a genuinely different `value` fixed that but left the X
    // button emitting twice (it calls onChange itself, and the flag was no
    // longer there to suppress the effect's copy), and left StrictMode's double
    // effect run disarming it before the second pass — a spurious
    // onChange(initialValue) on mount for every screen, under `npm start` only.
    // Comparing the two values has no state to get out of step.
    if (local === value) return;
    if (!debounce) {
      onChange?.(local);
      return;
    }
    timer.current = setTimeout(() => onChange?.(local), debounce);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local]);

  return (
    <div className={className}>
      <Input
        type="text"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        placeholder={placeholder ?? t('cm.search')}
        startAdornment={<FiSearch size={16} />}
        endAdornment={
          local ? (
            <button
              type="button"
              onClick={() => {
                setLocal('');
                onChange?.('');
              }}
              className="pointer-events-auto p-1 -m-1 text-content-muted hover:text-content rounded"
              aria-label={t('cm.clear')}
            >
              <FiX size={14} />
            </button>
          ) : undefined
        }
        {...rest}
      />
    </div>
  );
}
